import ko from 'knockout';
import repositoryHelper from './repositoryHelper';
import UploadModel from '../Models/uploadModel';
import uploadManager from '../Utils/uploadManager';
import constants from '../constants';
import moment from 'moment';

function UploadRepository() {

  var self = this;
  var baseResourceUrl = 'api/upload/';

  /**
   * Gets a list of uploads for a given request Id. The uploads are retrieved from the server and either
   * returned directly or used to lookup active / pending uploads from the upload manager which are
   * returned instead.
   * @param {string} requestId
   * @return {UploadModel[]}
   */
  self.getUploadsByRequestId = function (requestId) {

    var resourceUrl = baseResourceUrl + '?requestId=' + requestId;

    return new Promise(function (resolve, reject) {

      repositoryHelper.ajaxGet(resourceUrl)
        .then(function (serverUploads) {
          var uploads = createUploadModels(serverUploads);
          resolve(uploads);
        })
        .catch(function (jqXhr) {
          reject(jqXhr);
        });
    });
  };

  /**
   * Adds one or more files for upload. The newly created uploads are returned immediately. Asynchronously,
   * server calls are made to create the new uploads / generate upload tokens and then the upload engine
   * used to start the uploads.
   * @param {object} fileList An array of native browser File objects to be uploaded
   * @param {string} requestId
   * @param {Function} addUploadsFailedCallback Callback to be called with an error on failure
   * @return {Array}
   */
  self.addUploads = function (fileList, requestId, addUploadsFailedCallback) {

    var i;
    var file;
    var upload;
    var serverUpload = null;
    var files = [];
    var uploads = [];

    for (i = 0; i < fileList.length; i++) {
      file = fileList[i];
      files.push(file);

      // ReSharper disable once ExpressionIsAlwaysConst
      upload = new UploadModel(serverUpload, file);
      uploads.push(upload);
    }

    // Asynchronously create each upload on the server / generate upload token then start the uploads
    postNewUploadsToServer(files, requestId)
      .then(function (serverUploads) {
        startUploads(uploads, serverUploads);
      })
      .catch(function (e) {

        var upload;

        for (i = 0; i < uploads.length; i++) {
          upload = uploads[i];
          upload.setUploadFailedAuthorisation();
        }

        addUploadsFailedCallback(e);
      });

    return uploads;
  };

  /**
   * Deletes an upload by making a call to the service and cancels an active or pending upload
   * @param {UploadModel} upload
   * @return {Promise}
   */
  self.deleteUpload = function (upload) {

    var resourceUrl = baseResourceUrl + 'deleteItem/' + upload.uploadId;

    if (upload.statusName() === constants.uploadStatuses.pending ||
      upload.statusName() === constants.uploadStatuses.uploading) {

      uploadManager.cancelUpload(upload);
    }

    // If the upload failed authorisation then there is no upload to delete so return with a resolved
    // promise. For uploads that are authorising the user can't remove the upload.
    if (upload.statusName() === constants.uploadStatuses.failedAuthorisation) {
      return Promise.resolve(null);
    }

    return repositoryHelper.ajaxDelete(resourceUrl);
  };

  /**
   * Deletes an uploads by making a call to the service and cancels an active or pending uploads
   * @param {UploadModel[]} uploads
   * @return {Promise}
   */
  self.deleteUploads = function (uploads) {

    var resourceUrl = baseResourceUrl + 'deleteItems';

    ko.utils.arrayForEach(uploads, function (upload) {
      if (upload.statusName() === constants.uploadStatuses.pending ||
        upload.statusName() === constants.uploadStatuses.uploading) {

        uploadManager.cancelUpload(upload);
      }
    });

    // If the upload failed authorisation then there is no upload to delete.
    var uploadsForDelete = ko.utils.arrayFilter(uploads, function (upload) {
      return upload.statusName() !== constants.uploadStatuses.failedAuthorisation;
    }).map(function (upload) {
      return upload.uploadId;
    });

    // If there is no upload to delete so return with a resolved promise.
    // For uploads that are authorising the user can't remove the upload.
    if (uploadsForDelete.length === 0) {
      return Promise.resolve(null);
    }

    return repositoryHelper.ajaxPost(resourceUrl, uploadsForDelete);
  };

  /**
   * Takes an array of uploads and updates each via the server if the camera name has been changed
   * @param {UploadModel[]} uploads
   * @return {Promise}
   */
  self.updateUploads = function (uploads) {

    var i;
    var upload;
    var resourceUrl;
    var uploadData;
    var updateUploadPromises = [];

    for (i = 0; i < uploads.length; i++) {
      upload = uploads[i];

      if (!upload.hasChanged()) {
        continue;
      }

      resourceUrl = baseResourceUrl + upload.uploadId;
      uploadData = ko.toJS(upload);

      updateUploadPromises.push(repositoryHelper.ajaxPut(resourceUrl, uploadData));
    }

    return Promise.all(updateUploadPromises);
  };

  /**
   * Start each upload after all entities created / upload tokens generated on the server
   * @param {UploadModel[]} uploads
   * @param {*} serverUploads Array of server models including upload token property
   */
  function startUploads(uploads, serverUploads) {

    var serverUpload;
    var upload;

    for (var i = 0; i < serverUploads.length; i++) {
      serverUpload = serverUploads[i];
      upload = uploads[i];

      upload.updateAfterServerModelCreated(serverUpload);
      uploadManager.addUpload(upload);
    }
  }

  function postNewUploadsToServer(files, requestId) {

    var resourceUrl = baseResourceUrl + 'uploadItems';

    var data = {
      requestId: requestId,
      uploads: files.map(function (file) {
        return {
          fileName: file.name,
          fileSize: file.size,
          lastWriteDate: moment(file.lastModified || file.lastModifiedDate).toISOString(),
          fileRelativePath: file.webkitRelativePath
        };
      })
    };

    return repositoryHelper.ajaxPost(resourceUrl, data);
  }

  function createUploadModels(serverUploads) {

    var serverUpload;
    var uploads = [];

    for (var i = 0; i < serverUploads.length; i++) {
      serverUpload = serverUploads[i];
      uploads.push(getOrCreateUploadModel(serverUpload));
    }

    return uploads;
  }

  function getOrCreateUploadModel(serverUpload) {
    var upload = uploadManager.getUpload(serverUpload.fileName, serverUpload.requestId);
    return upload ? upload : new UploadModel(serverUpload);
  }
}

export default new UploadRepository();
